import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { fencedFetch } from './accountChannel'

/**
 * A Supabase client whose every request carries ONE access token, so a
 * multi-step operation (Remove's delete and verification read, every
 * saved_collections read and upsert) is pinned to the user that token belongs
 * to, whatever the shared client's session does meanwhile (a sign-out or an
 * account switch in another tab).
 *
 * Built with supabase-js's `accessToken` option: there is no auth client
 * behind it at all, so nothing is persisted, refreshed or listened to. Cached
 * per token (a handful at most), so a call does not construct one. Null when
 * Supabase is not configured.
 */
const MAX_PINNED_CLIENTS = 4
const pinnedClients = new Map<string, SupabaseClient>()

export function supabaseForToken(accessToken: string): SupabaseClient | null {
  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || !anonKey || !accessToken) return null
  const cached = pinnedClients.get(accessToken)
  if (cached) return cached
  const client = createClient(url, anonKey, { accessToken: async () => accessToken, global: { fetch: fencedFetch } })
  pinnedClients.set(accessToken, client)
  if (pinnedClients.size > MAX_PINNED_CLIENTS) {
    const oldest = pinnedClients.keys().next().value
    if (oldest !== undefined) pinnedClients.delete(oldest)
  }
  return client
}

/**
 * Clear the legacy collection fields in ONE user's user_metadata, as that user.
 *
 * Calls Supabase Auth directly (PUT /auth/v1/user) with that user's access
 * token, never the shared client: the shared client acts as whoever its
 * session is NOW, so an account switch after the caller's identity check would
 * clear the wrong account's metadata. False when Supabase is not configured,
 * the request fails, or the response is not OK.
 */
export async function clearLegacyCollectionMetadataAs(accessToken: string): Promise<boolean> {
  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || !anonKey) return false
  try {
    const res = await fetch(`${String(url).replace(/\/+$/, '')}/auth/v1/user`, {
      method: 'PUT',
      headers: {
        // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
        'Content-Type': 'application/json',
        apikey: anonKey,
        // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        data: {
          collection_id: null,
          owned_cards: null,
          collection_card_count: null,
          collection_source_url: null,
        },
      }),
    })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Read ONE user's legacy user_metadata.collection_id, as that user (GET
 * /auth/v1/user with their own token, never the shared client).
 *
 *   { ok: true, collectionId } - read; null when no legacy pointer is set
 *   { ok: false }              - could not read (unconfigured, network, non-OK,
 *                                unparseable): the caller must not assume
 */
export async function readLegacyCollectionIdAs(
  accessToken: string,
): Promise<{ ok: true; collectionId: string | null } | { ok: false }> {
  const url = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || !anonKey) return { ok: false }
  try {
    const res = await fetch(`${String(url).replace(/\/+$/, '')}/auth/v1/user`, {
      method: 'GET',
      headers: {
        apikey: anonKey,
        // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
        Authorization: `Bearer ${accessToken}`,
      },
    })
    if (!res.ok) return { ok: false }
    const body = await res.json() as { user_metadata?: Record<string, unknown> } | null
    const id = body?.user_metadata?.collection_id
    return { ok: true, collectionId: typeof id === 'string' && id ? id : null }
  } catch {
    return { ok: false }
  }
}
